import { useEffect, useRef } from 'react';
import { Users, UserCheck } from 'lucide-react';
import { reloadAfterMutation } from '../../lib/embedSafeReload';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';

interface PromotePlayerModalProps {
  show: boolean;
  playerName: string;
  onPromoteToMain: () => Promise<{ success: boolean }> | { success: boolean };
  onPromoteToScratch: () => Promise<{ success: boolean }> | { success: boolean };
  onClose: () => void;
  isProcessing: boolean;
  language: 'en' | 'fr';
  allowScratch?: boolean;
}

export function PromotePlayerModal({
  show,
  playerName,
  onPromoteToMain,
  onPromoteToScratch,
  onClose,
  isProcessing,
  language,
  allowScratch = true
}: PromotePlayerModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      // 👇 On force le scroll vers l'élément
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && show) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onClose]);

  if (!show) return null;

  const titleText = language === 'fr' ? 'Promouvoir le Joueur' : 'Promote Player';
  const questionText = language === 'fr' 
    ? `Où voulez-vous promouvoir ${playerName} ?`
    : `Where do you want to promote ${playerName}?`;
  const mainClubText = language === 'fr' ? 'Club Principal' : 'Main Club';
  const scratchText = language === 'fr' ? 'Scratch Players' : 'Scratch Players';
  const cancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const processingText = language === 'fr' ? 'Traitement en cours...' : 'Processing...';
  const mainClubDesc = language === 'fr' 
    ? 'Joueur actif dans l\'équipe principale'
    : 'Active player in the main team';
  const scratchDesc = language === 'fr'
    ? 'Joueur inactif sur la liste des réserves de l\'équipe'
    : 'Inactive player on the team\'s scratch list';

  // Rafraîchir la page uniquement si la promotion réussit
  // Rafraîchir la page uniquement si la promotion réussit (success === true)
  function isSuccessResult(obj: any): obj is { success: boolean } {
    return obj && typeof obj === 'object' && 'success' in obj && typeof obj.success === 'boolean';
  }

  const handlePromoteToMainClick = async () => {
    const result = onPromoteToMain();
    if (result instanceof Promise) {
      const awaited = await result;
      if (isSuccessResult(awaited) && awaited.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
      // sinon, ne rien faire
    } else if (isSuccessResult(result) && result.success) {
      setTimeout(() => {
        reloadAfterMutation();
      }, 1500);
    }
    // sinon, ne rien faire
  };

  const handlePromoteToScratchClick = async () => {
    const result = onPromoteToScratch();
    if (result instanceof Promise) {
      const awaited = await result;
      if (isSuccessResult(awaited) && awaited.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
      // sinon, ne rien faire
    } else if (isSuccessResult(result) && result.success) {
      setTimeout(() => {
        reloadAfterMutation();
      }, 1500);
    }
    // sinon, ne rien faire
  };

  return (
    <MyTeamsModal onClose={onClose} busy={isProcessing}>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <Users size={20} className="me-2 text-primary" />
                {titleText}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-4">{questionText}</p>
              
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4"
                    onClick={handlePromoteToMainClick}
                    disabled={isProcessing}
                  >
                    <Users size={32} className="mb-2" />
                    <strong>{mainClubText}</strong>
                    <small className="text-muted mt-1 text-center">{mainClubDesc}</small>
                  </button>
                </div>
                
                {allowScratch && (
                  <div className="col-12 col-md-6">
                    <button
                      type="button"
                      className="btn btn-outline-warning w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4"
                      onClick={handlePromoteToScratchClick}
                      disabled={isProcessing}
                    >
                      <UserCheck size={32} className="mb-2" />
                      <strong>{scratchText}</strong>
                      <small className="text-muted mt-1 text-center">{scratchDesc}</small>
                    </button>
                  </div>
                )}
              </div>
              
              {isProcessing && (
                <div className="text-center mt-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                  {processingText}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                {cancelText}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
        onClick={onClose}
      ></div>
    </MyTeamsModal>
  );
}
